import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapResponseError } from '@mybusinessmate/api-client';
import type {
  AssignAccountManagerInput,
  BusinessDetail,
  BusinessListResponse,
  BusinessTab,
  StaffDetail,
  StaffListResponse,
  StaffRole,
  StaffRow,
  StaffStatus,
  SuspendStaffInput,
  UpdateStaffInput,
} from '@mybusinessmate/schema';
import { api } from './api';

/** Every business and staff query hangs off this key, so one invalidate refreshes lists, details and counts. */
export const directoryKey = ['directory'] as const;

const PAGE_SIZE = 25;

// --- Businesses -------------------------------------------------------------------------------------------------

export interface BusinessFilters {
  tab: BusinessTab;
  q?: string;
  /** A staff id, or 'none' for businesses without an account manager. */
  amId?: string;
}

export const businessListQuery = (filters: BusinessFilters) =>
  infiniteQueryOptions({
    queryKey: [...directoryKey, 'businesses', filters] as const,
    queryFn: async ({ pageParam }): Promise<BusinessListResponse> => {
      const res = await api.api.businesses.$get({
        query: {
          tab: filters.tab,
          limit: String(PAGE_SIZE),
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.amId ? { amId: filters.amId } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const businessQuery = (id: string) =>
  queryOptions({
    queryKey: [...directoryKey, 'business', id] as const,
    queryFn: async (): Promise<BusinessDetail> => {
      const res = await api.api.businesses[':id'].$get({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

export function useAssignAccountManager(businessId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (json: AssignAccountManagerInput) => {
      const res = await api.api.admin.businesses[':id']['account-manager'].$post({ param: { id: businessId }, json });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    // The list rows, the tab badges, the business page and every staff member's client count all change.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: directoryKey }),
  });
}

// --- Staff ------------------------------------------------------------------------------------------------------

export interface StaffFilters {
  role?: StaffRole;
  status?: StaffStatus;
  q?: string;
}

export const staffListQuery = (filters: StaffFilters) =>
  infiniteQueryOptions({
    queryKey: [...directoryKey, 'staff', filters] as const,
    queryFn: async ({ pageParam }): Promise<StaffListResponse> => {
      const res = await api.api.admin.staff.$get({
        query: {
          limit: String(PAGE_SIZE),
          ...(filters.role ? { role: filters.role } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const staffQuery = (id: string) =>
  queryOptions({
    queryKey: [...directoryKey, 'staff-member', id] as const,
    queryFn: async (): Promise<StaffDetail> => {
      const res = await api.api.admin.staff[':id'].$get({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

/** Active account managers and admins: the people a business can be assigned to, alphabetical. */
export const accountManagerCandidatesQuery = queryOptions({
  queryKey: [...directoryKey, 'am-candidates'] as const,
  queryFn: async (): Promise<StaffRow[]> => {
    const fetchRole = async (role: StaffRole): Promise<StaffRow[]> => {
      const res = await api.api.admin.staff.$get({ query: { role, status: 'active', limit: '50' } });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()).items;
    };
    const [managers, admins] = await Promise.all([fetchRole('account_manager'), fetchRole('admin')]);
    return [...managers, ...admins].sort((a, b) => a.name.localeCompare(b.name));
  },
});

export function useUpdateStaff(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (json: UpdateStaffInput): Promise<StaffDetail> => {
      const res = await api.api.admin.staff[':id'].$put({ param: { id }, json });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: directoryKey }),
  });
}

export function useSuspendStaff(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (json: SuspendStaffInput) => {
      const res = await api.api.admin.staff[':id'].suspend.$post({ param: { id }, json });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    // Their open work moved, so task and project views are stale too.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useReinstateStaff(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<StaffDetail> => {
      const res = await api.api.admin.staff[':id'].reinstate.$post({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: directoryKey }),
  });
}
